// Marca do app: mini logo (telefone) + wordmark "Disk". Reutilizada no login e na sidebar.
//
// White label: dentro do app autenticado a marca visível é a da ORGANIZATION
// ativa, não a do produto — `AppBrandMark` resolve isso num lugar só, para que
// sidebar e menu mobile não caiam em fallbacks diferentes. As telas públicas
// sem marca (login, termos, recuperação de senha) continuam mostrando o
// produto, porque ali ainda não se sabe de qual cliente é a pessoa.
import { useEffect } from "react";
import { Phone } from "lucide-react";
import { cn } from "@/lib/utils";
import { useActiveOrganization } from "@/lib/auth-client";
import { applyBrandColor, readBrandColor } from "@/lib/brand-theme";

// Logo oficial (SVG), sempre a variante /logo.svg — a de gradiente saturado.
// Não segue mais o tema: /logo-light.svg é a mesma arte lavada (fill-opacity 0.6
// e cinzas), e como o gradiente é azul/roxo — não branco — a variante saturada
// contrasta bem nos dois temas. Asset em public/.
export function Logo({ className, src, alt }: { className?: string; src?: string; alt?: string }) {
  return <img src={src || "/logo.svg"} alt={alt ?? "Disk"} className={className} />;
}

export function Brand({ size = "sm", className }: { size?: "sm" | "lg"; className?: string }) {
  const lg = size === "lg";
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <span
        className={cn(
          "flex items-center justify-center rounded-md bg-primary text-primary-foreground",
          lg ? "size-9" : "size-6",
        )}
      >
        <Phone className={lg ? "size-5" : "size-3.5"} />
      </span>
      <span className={cn("font-semibold tracking-tight", lg ? "text-lg" : "text-sm")}>Disk</span>
    </div>
  );
}

/**
 * Aplica a cor da marca no <html> enquanto o componente estiver montado, e a
 * retira ao desmontar. A limpeza importa: sair do app para uma tela pública (ou
 * trocar para uma marca sem cor) tem que voltar ao tema do produto em vez de
 * manter a cor do cliente anterior pendurada no documento.
 */
export function useBrandColor(color: string | null | undefined): void {
  useEffect(() => {
    applyBrandColor(color);
    return () => applyBrandColor(null);
  }, [color]);
}

/** A cor da marca ATIVA. Fora do app autenticado (sem org) é `null`. */
export function useActiveBrandColor(): string | null {
  const { data: org } = useActiveOrganization();
  return readBrandColor((org as { metadata?: unknown } | null)?.metadata);
}

/**
 * Logo + nome da marca ativa, com o produto como fallback. O logo é o mesmo
 * campo que /settings/brand já editava (`organization.logo`), que até aqui só
 * aparecia na própria tela de configuração.
 *
 * O nome só troca junto com o logo: uma marca sem logo mostrando "Disk" ao lado
 * do símbolo do produto é o app de sempre, mas o nome do cliente ao lado do
 * símbolo do Disk seria uma marca que não existe.
 */
export function AppBrandMark({ className }: { className?: string }) {
  const { data: org } = useActiveOrganization();
  const logo = org?.logo?.trim() || null;
  const name = logo ? (org?.name ?? "Disk") : "Disk";
  return (
    <div className={cn("flex min-w-0 items-center gap-2", className)}>
      <Logo
        src={logo ?? undefined}
        alt={name}
        className={cn("h-8 w-auto shrink-0", logo && "max-w-32 object-contain")}
      />
      <span className="truncate text-base font-semibold tracking-tight">{name}</span>
    </div>
  );
}
